/**
 * Shared LO Payout Ledger Builder
 * Pure function — no React, no hooks.
 * Used by: email payload, CalculationBreakdown (Accounting tab), and can be adopted elsewhere.
 *
 * Standardizes on 4 decimal places for percentage values to match banker recon pricing precision.
 */

export interface LoPayoutLedgerLine {
  label: string;
  amount: number;
  percent: string | null; // (abs(amount) / loanAmount) * 100, toFixed(4)
  type: 'base' | 'deduction' | 'reimbursable' | 'volumeBonus' | 'subtotal' | 'eAndO' | 'total';
}

export interface LoPayoutLedger {
  lines: LoPayoutLedgerLine[];
  subtotal: number;
  subtotalPercent: string | null;
  netPayout: number;
}

function toBps(amount: number, loanAmount: number): string | null {
  if (loanAmount <= 0) {
return null;
}
  return ((Math.abs(amount) / loanAmount) * 100).toFixed(4);
}

export function buildLoPayoutLedger(params: {
  loanAmount: number;
  baseCompensation: number;
  deductions: { label: string; amount: number }[];
  reimbursables: { label: string; amount: number }[];
  eAndODeduction: number;
  volumeBonusAmount?: number;
  volumeBonusLabel?: string;
}): LoPayoutLedger {
  const { loanAmount, baseCompensation, deductions, reimbursables, eAndODeduction } = params;
  const volumeBonusAmount = params.volumeBonusAmount ?? 0;
  if (!Number.isFinite(volumeBonusAmount) || volumeBonusAmount < 0) {
    throw new Error('Volume bonus must be a non-negative finite amount.');
  }

  const lines: LoPayoutLedgerLine[] = [];

  // Base Compensation
  lines.push({
    label: 'Base Compensation',
    amount: baseCompensation,
    percent: toBps(baseCompensation, loanAmount),
    type: 'base'
  });

  // Named deductions
  const totalDeductions = deductions.reduce((sum, d) => sum + d.amount, 0);
  for (const d of deductions) {
    lines.push({
      label: d.label,
      amount: -d.amount,
      percent: toBps(d.amount, loanAmount),
      type: 'deduction'
    });
  }

  // Post-deduction comp is signed so deductions reduce the entire payable ledger.
  const postDeductionComp = baseCompensation - totalDeductions;

  // Named reimbursables (include all, positive and negative)
  const totalReimbursables = reimbursables.reduce((sum, r) => sum + r.amount, 0);
  for (const r of reimbursables) {
    lines.push({
      label: r.label,
      amount: r.amount,
      percent: toBps(r.amount, loanAmount),
      type: 'reimbursable'
    });
  }

  // Subtotal (between reimbursables and E&O)
  if (volumeBonusAmount > 0) {
    lines.push({ label: params.volumeBonusLabel || 'Volume Bonus', amount: volumeBonusAmount, percent: null, type: 'volumeBonus' });
  }
  const subtotal = postDeductionComp + totalReimbursables + volumeBonusAmount;
  const subtotalPercent = toBps(subtotal, loanAmount);
  lines.push({
    label: 'Subtotal',
    amount: subtotal,
    percent: subtotalPercent,
    type: 'subtotal'
  });

  // E&O
  if (eAndODeduction > 0) {
    lines.push({
      label: 'E&O Insurance',
      amount: -eAndODeduction,
      percent: null,
      type: 'eAndO'
    });
  }

  // Net Payout
  const netPayout = subtotal - eAndODeduction;
  lines.push({
    label: 'Net Payout',
    amount: netPayout,
    percent: toBps(netPayout, loanAmount),
    type: 'total'
  });

  return { lines, subtotal, subtotalPercent, netPayout };
}
